/**
 * Application entry point.
 *
 * initMsal() is awaited before React renders so that:
 *   - handleRedirectPromise() is processed (consuming the auth-code redirect)
 *   - setActiveAccount() is called (§11.8 single-account policy)
 *   - msalInstance.getActiveAccount() is reliable for the HTTP interceptor
 *     and Socket.IO auth callback from the first render onward
 *
 * The root's three error callbacks are the app's only reporter for failures
 * React observes; supplying them replaces React's own console reporting, so
 * every failure below produces exactly one log entry.
 */

import React from 'react'
import ReactDOM from 'react-dom/client'
import { initMsal } from './services/msal.ts'
import App from './App.tsx'
import {
  reportCaughtError,
  reportRecoverableError,
  reportUncaughtError,
} from './utils/rootErrorReporting.ts'
import './index.css'
import { initializeTheme } from './theme/preference'

initializeTheme()

initMsal()
  .catch((err) => {
    // MSAL bootstrap failure (e.g. Entra unreachable at startup).
    // Log and continue — App renders with no active account; RequireAuth
    // will trigger loginRedirect once it mounts.
    console.error('MSAL initialization failed:', err)
  })
  .finally(() => {
    ReactDOM.createRoot(document.getElementById('root')!, {
      onCaughtError: reportCaughtError,
      onRecoverableError: reportRecoverableError,
      onUncaughtError: reportUncaughtError,
    }).render(
      <React.StrictMode>
        <App />
      </React.StrictMode>,
    )
  })
